import { useState } from "react"
import { useSuspenseQuery } from "@tanstack/react-query"
import { Link, createFileRoute, redirect } from "@tanstack/react-router"
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Clock3,
  GitBranch,
  MessageCircle,
  Plus,
  Send,
  Users,
  Workflow,
} from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardFooter } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { AutoHeading, Note } from "@/components/automations/primitives"
import { templatesQuery, workspaceQuery } from "@/lib/queries"
import { useWorkspaceContext } from "@/lib/session"
import "@/styles/automation-polish.css"

export const Route = createFileRoute("/w/$workspace/automations/templates")({
  beforeLoad: async ({ context, params }) => {
    const ctx = await context.queryClient.ensureQueryData(workspaceQuery(params.workspace))
    if (!ctx.permissions.includes("edit_automations"))
      throw redirect({ to: "/w/$workspace/automations", params })
  },
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData(templatesQuery(params.workspace)),
  component: TemplateLibrary,
})

const ICONS: Record<string, typeof MessageCircle> = {
  comment_to_dm: MessageCircle,
  welcome: Send,
  route: GitBranch,
  qualify: Users,
  after_hours: Clock3,
  blank: Plus,
}

function TemplateArtwork({ templateKey }: { templateKey: string }) {
  const Icon = ICONS[templateKey] ?? Plus
  const EntryIcon = templateKey === "after_hours" ? Clock3 : MessageCircle
  const OutcomeIcon = templateKey === "route" || templateKey === "qualify" ? Users : Send
  return (
    <div className="auto-template-art" data-template={templateKey} aria-hidden="true">
      <div className="auto-art-orbit" />
      <div className="auto-art-path" />
      <span className="auto-art-node auto-art-entry">
        <EntryIcon />
      </span>
      <span className="auto-art-node auto-art-center">
        <Icon />
      </span>
      <span className="auto-art-node auto-art-outcome">
        <OutcomeIcon />
        <span className="auto-art-check">
          <Check />
        </span>
      </span>
    </div>
  )
}

/** Screen 16. */
function TemplateLibrary() {
  const { workspace } = useWorkspaceContext()
  const templates = useSuspenseQuery(templatesQuery(workspace.slug)).data
  const [category, setCategory] = useState("All templates")
  const [query, setQuery] = useState("")
  const params = { workspace: workspace.slug }
  const filtered = templates.filter(
    (t) =>
      (category === "All templates" || t.category === category) &&
      t.name.toLowerCase().includes(query.toLowerCase())
  )
  return (
    <div className="page-wrap auto-page auto-template-library">
      <AutoHeading
        title="Start with a conversation"
        description="Choose a template. Make it sound like you."
        back={
          <Button variant="ghost" size="sm" className="mb-3 -ml-2" asChild>
            <Link to="/w/$workspace/automations" params={params}>
              <ArrowLeft />
              Automations
            </Link>
          </Button>
        }
      >
        <Button variant="outline" asChild>
          <Link to="/w/$workspace/automations/new" params={params} search={{ template: "blank" }}>
            <Plus />
            Build from scratch
          </Link>
        </Button>
      </AutoHeading>
      <div className="auto-library-intro">
        <span className="auto-library-intro-icon" aria-hidden="true">
          <Workflow />
        </span>
        <div>
          <p>A thoughtful starting point. Your personal touch.</p>
          <span>Each journey is yours to shape, from the first message to the handover.</span>
        </div>
        <Badge variant="secondary">Start as a draft</Badge>
      </div>
      <div className="auto-toolbar mb-6">
        <Tabs value={category} onValueChange={setCategory}>
          <TabsList>
            {["All templates", "Lead capture", "Support"].map((t) => (
              <TabsTrigger key={t} value={t}>
                {t}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <Input
          aria-label="Search templates"
          placeholder="Find a template…"
          className="sm:w-60"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>
      <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
        {filtered.map((t) => {
          return (
            <Card key={t.key} className="auto-library-card gap-0">
              <div className="auto-template-cover">
                <TemplateArtwork templateKey={t.key} />
                {t.recommended && (
                  <Badge className="auto-recommended" variant="secondary">
                    Recommended
                  </Badge>
                )}
              </div>
              <CardContent className="auto-template-copy">
                <span className="auto-template-category">{t.category}</span>
                <h2 className="text-base font-medium">{t.name}</h2>
                <p className="mt-2 min-h-16 text-sm leading-6 text-muted-foreground">
                  {t.description}
                </p>
                <div className="auto-template-steps">
                  {t.nodes.map((node, index) => (
                    <span key={`${index}-${node}`} className="contents">
                      {index > 0 && <ArrowRight className="size-3 text-muted-foreground" />}
                      <span className="auto-template-step">{node}</span>
                    </span>
                  ))}
                </div>
              </CardContent>
              <CardFooter className="auto-template-footer mt-auto justify-between gap-2">
                <span className="text-xs text-muted-foreground">{t.channel}</span>
                <Button variant="outline" size="sm" asChild>
                  <Link
                    to="/w/$workspace/automations/new"
                    params={params}
                    search={{ template: t.key }}
                    aria-label={`Use ${t.name} template`}
                  >
                    Use template
                    <ArrowRight />
                  </Link>
                </Button>
              </CardFooter>
            </Card>
          )
        })}
      </div>
      {!filtered.length && (
        <p className="py-16 text-center text-muted-foreground">
          No matching templates. Try a different search.
        </p>
      )}
      <Note>
        Templates are drafts. Review the channel, message and contact entry rules before publishing.
      </Note>
    </div>
  )
}
